import { useState } from "react";
import { Search, SlidersHorizontal, LayoutGrid, List as ListIcon, Clock, ArrowRight } from "lucide-react";

const operators = [
  {
    id: "OPR-2411-014",
    name: "Ramesh Naidu",
    role: "CNC Machinist",
    centre: "APIC Visakhapatnam",
    status: "In Production",
    task: "Manufacturing",
    pct: 62,
    eta: "Nov 28",
    project: "EV Battery Enclosure v2",
  },
  {
    id: "OPR-2411-021",
    name: "Priya Kumari",
    role: "Quality Engineer",
    centre: "APIC Vijayawada",
    status: "Inspection",
    task: "Quality Inspection",
    pct: 88,
    eta: "Nov 22",
    project: "Agri Drone Chassis",
  },
  {
    id: "OPR-2411-009",
    name: "Suresh Babu",
    role: "Procurement Lead",
    centre: "APIC Tirupati",
    status: "Awaiting Approval",
    task: "Quotation",
    pct: 25,
    eta: "Dec 06",
    project: "IoT Smart Water Sensor",
  },
  {
    id: "OPR-2411-033",
    name: "Anita Reddy",
    role: "Design Reviewer",
    centre: "APIC Visakhapatnam",
    status: "Review",
    task: "CAD Review",
    pct: 15,
    eta: "Dec 12",
    project: "Medical Infusion Housing",
  },
  {
    id: "OPR-2411-005",
    name: "Vikram Rao",
    role: "Assembly Technician",
    centre: "APIC Kurnool",
    status: "Delivered",
    task: "Delivered",
    pct: 100,
    eta: "Nov 10",
    project: "Robotic Gripper Assembly",
  },
  {
    id: "OPR-2411-047",
    name: "Lakshmi Devi",
    role: "Floor Supervisor",
    centre: "APIC Kurnool",
    status: "Idle",
    task: "Awaiting Assignment",
    pct: 0,
    eta: "—",
    project: "Unassigned",
  },
];

const STATUS_FILTERS = [
  "All",
  "In Production",
  "Inspection",
  "Awaiting Approval",
  "Review",
  "Delivered",
  "Idle",
];

const STATUS_STYLES = {
  "In Production": "bg-sky-100 text-sky-700",
  "Inspection": "bg-teal-100 text-teal-700",
  "Awaiting Approval": "bg-orange-100 text-orange-700",
  "Review": "bg-violet-100 text-violet-700",
  "Delivered": "bg-green-100 text-green-700",
  "Idle": "bg-slate-100 text-slate-600",
};

function initials(name) {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function OperatorCard({ op, listView }) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-150 flex flex-col ${
        listView ? "w-full" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <span className="font-mono text-xs border border-slate-200 rounded-full px-3 py-1 text-slate-600 whitespace-nowrap">
          {op.id}
        </span>
        <span
          className={`text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap ${STATUS_STYLES[op.status]}`}
        >
          {op.status}
        </span>
      </div>

      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 text-white font-bold text-base flex items-center justify-center flex-shrink-0">
          {initials(op.name)}
        </div>
        <div>
          <p className="text-lg font-extrabold text-slate-900 leading-tight">{op.name}</p>
          <p className="text-sm text-slate-500">
            {op.role} · {op.centre}
          </p>
        </div>
      </div>

      <p className="text-sm text-slate-500 mt-3">
        Working on: <span className="font-semibold text-slate-900">{op.project}</span>
      </p>

      <div className="flex justify-between items-baseline mt-4 text-sm font-semibold text-slate-700">
        <span>{op.task}</span>
        <span className="text-lg font-extrabold text-slate-900">{op.pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
        <div
          className="h-full bg-slate-900 rounded-full transition-all duration-300"
          style={{ width: `${op.pct}%` }}
        />
      </div>

      <div className="flex justify-between items-center mt-5 pt-4 border-t border-slate-100 text-sm text-slate-500">
        <span className="flex items-center gap-1.5">
          <Clock size={15} />
          ETA {op.eta}
        </span>
        <a
          href="#"
          onClick={(e) => e.preventDefault()}
          className="flex items-center gap-1 text-blue-600 font-bold hover:underline"
        >
          Open workspace <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

export default function MyOperators() {
  const [search, setSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("All");
  const [view, setView] = useState("grid");

  const term = search.trim().toLowerCase();

  const filtered = operators.filter((o) => {
    const matchesStatus = activeStatus === "All" || o.status === activeStatus;
    const matchesSearch =
      !term ||
      o.name.toLowerCase().includes(term) ||
      o.id.toLowerCase().includes(term) ||
      o.project.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  return (
    <div className=" ">
      <div className="max-w-6xl mx-auto" style={{height:'90vh',overflow:'auto',scrollbarWidth:"none"}}>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-1">
          My Operators
        </h1>
        <p className="text-slate-500 mb-6">
          {operators.length} operators across 4 APIC centres
        </p>

        {/* Toolbar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-5 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-3 flex-1">
              <Search size={18} className="text-slate-400 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search operator name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              />
            </div>

            <button className="flex items-center justify-center gap-2 border border-slate-200 rounded-xl px-4 py-3 font-semibold text-sm text-slate-900 hover:bg-slate-50">
              <SlidersHorizontal size={16} />
              Filters
            </button>

            <div className="flex border border-slate-200 rounded-xl overflow-hidden self-end sm:self-auto">
              <button
                onClick={() => setView("grid")}
                className={`p-3 ${
                  view === "grid"
                    ? "bg-gradient-to-br from-indigo-500 to-blue-500 text-white"
                    : "bg-white text-slate-400"
                }`}
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setView("list")}
                className={`p-3 ${
                  view === "list"
                    ? "bg-gradient-to-br from-indigo-500 to-blue-500 text-white"
                    : "bg-white text-slate-400"
                }`}
              >
                <ListIcon size={18} />
              </button>
            </div>
          </div>

          {/* Status tabs */}
          <div className="flex flex-wrap gap-2 mt-4">
            {STATUS_FILTERS.map((s) => {
              const count =
                s === "All" ? operators.length : operators.filter((o) => o.status === s).length;
              const active = activeStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => setActiveStatus(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    active
                      ? "bg-gradient-to-br from-indigo-500 to-blue-500 text-white border-transparent"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {s} <span className="opacity-75 font-bold text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-4 px-1">
          Showing {filtered.length} of {operators.length} operators
        </p>

        {/* Grid / List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 px-5 bg-white border border-dashed border-slate-200 rounded-2xl text-slate-500">
            <h3 className="text-lg font-bold text-slate-900 mb-1">No operators found</h3>
            <p>Try a different search term or filter.</p>
          </div>
        ) : (
          <div
            className={
              view === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                : "flex flex-col gap-4"
            }
          >
            {filtered.map((op) => (
              <OperatorCard key={op.id} op={op} listView={view === "list"} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}